import { subscriptionApiAxiosInstance } from '@/helpers/request'

export default {
	namespaced: true,

	state: {
		subscription: null,
	},

	mutations: {
		setSubscription(state, subscription) {
			if (!subscription) {
				return
			}

			if (subscription.scheduled) {
				subscription.scheduled.phases[0].is_active = true
				subscription = {
					...subscription,
					...subscription.scheduled,
				}
			}
			state.subscription = subscription
		},
		subscriptionMarkCanceled(state) {
			state.subscription.cancelled = true
		},
	},

	getters: {
		hasSubscription: (state) => {
			return Boolean(state.subscription && Object.keys(state.subscription).length)
		},

		getSubscription: (state, getters) => {
			const phases = getters.getPhases
			const subscription = state.subscription

			if (!getters.hasSubscription) {
				return {}
			}

			return {
				...(phases.regular?.currency && { currency: phases.regular.currency }),
				...subscription,
				...phases,
			}
		},

		getCurrency: (state, getters) => {
			return getters.getSubscription?.currency?.toLowerCase?.()
		},

		getActivePhase: (state, getters) => {
			return Object.values(getters.getPhases).find((phase) => phase.is_active)
		},

		getPhases: (state) => {
			const phases = state.subscription?.phases ?? []

			return phases.reduce((map, phase) => {
				const iterationPrices = phase.iteration_prices ?? []
				const iterationPrice = iterationPrices.find(
					(price) => phase.is_active && price.iteration === state.subscription?.billing_period_number,
				)

				map[phase.phase_type] = {
					...phase,
					price: iterationPrice?.price ?? phase.price,
					discount: iterationPrice?.discount ?? null,
				}
				return map
			}, {})
		},

		checkPeriodType: (state, getters) => {
			const activePhase = getters.getActivePhase

			return {
				isCommitment: activePhase?.phase_type === 'commitment',
				isTrial: activePhase?.phase_type === 'trial',
				isRegular: activePhase?.phase_type === 'regular',
			}
		},

		checkProvider: (state, getters) => {
			const provider = getters.getSubscription?.provider

			return {
				isPalta: provider === 'palta',
				isStripe: provider === 'stripe',
				isBraintree: provider === 'braintree',
				isPrimer: provider === 'primer',
			}
		},

		checkStatus: (state, getters) => {
			return {
				isActive: Boolean(getters.hasSubscription && !state.subscription?.cancelled),
				isPaused: Boolean(state.subscription?.is_paused),
				isCancelled: Boolean(state.subscription?.cancelled),
				isExpired: Boolean(state.subscription?.is_expired),
				isOverdue: Boolean(state.subscription?.is_overdue),
			}
		},

		checkBillingIntervalType: (state, getters) => {
			return {
				isWeek: getters.getSubscription?.regular.interval_unit === 'week',
				isMonth: getters.getSubscription?.regular.interval_unit === 'month',
				isYear: getters.getSubscription?.regular.interval_unit === 'year',
				isDays: getters.getSubscription?.regular.interval_unit === 'days',
			}
		},

		isTrialEqual: (state, getters) => (duration, unit) => {
			return (
				getters.getSubscription?.trial?.interval_duration === duration &&
				getters.getSubscription?.trial?.interval_unit === unit
			)
		},

		getIsLegacy: (state, getters) => {
			return [getters.checkProvider.isStripe, getters.checkProvider.isBraintree].some(Boolean)
		},

		getIsModern: (state, getters) => {
			return [getters.checkProvider.isPalta, getters.checkProvider.isPrimer].some(Boolean)
		},

		// Can use features
		canUpgrade: (state, getters) => {
			return getters.getIsModern && !getters.checkPeriodType.isRegular
		},

		canPause: (state, getters) => {
			return getters.getIsModern && getters.getSubscription.is_pause_allowed
		},

		canResume: (state, getters) => {
			return getters.getSubscription.is_paused
		},

		canPurchaseCancelOffer: (state, getters) => {
			return getters.getSubscription.is_cancel_offer_allowed
		},

		canUseFeature: (state, getters) => (feature) => {
			switch (feature) {
				case 'upgrade':
					return getters.canUpgrade
				case 'pause':
					return getters.canPause
				case 'resume':
					return getters.canResume
				case 'purchaseCancelOffer':
					return getters.canPurchaseCancelOffer
				default:
					return false
			}
		},
	},

	actions: {
		async fetchSubscription({ commit, getters }, { customer_id }) {
			try {
				const response = await subscriptionApiAxiosInstance.get(`/billing/v2/users/${customer_id}/subscription`, {
					timeout: 5000,
				})
				commit('setSubscription', response.data)
			} catch (error) {
				commit('setSubscription', null)
			}

			return getters.getSubscription
		},
	},
}
